'use server'

import { z } from 'zod'

import { resolveOrgFromEmail, sanitizeRedirect } from './queries'

const resolveOrgSchema = z.object({
  email: z.email(),
  redirectTo: z.string().optional().nullable()
})

/**
 * Used by the generic /sign-in form. Resolves the user's email to an org
 * slug and returns the URL to navigate to. Returns null on no-match so the
 * caller renders a neutral message — avoids leaking which emails exist.
 *
 * Return-based (not redirect-based) because the no-match path needs the
 * client to swallow the result and show the neutral message rather than
 * navigate.
 */
export async function resolveSignInTarget(input: {
  email: string
  redirectTo?: string | null
}): Promise<{ url: string | null }> {
  const parsed = resolveOrgSchema.safeParse(input)
  if (!parsed.success) return { url: null }

  const slug = await resolveOrgFromEmail(parsed.data.email)
  if (!slug) return { url: null }

  const params = new URLSearchParams({ email: parsed.data.email })
  const safeRedirect = sanitizeRedirect(parsed.data.redirectTo)
  if (safeRedirect) params.set('redirectTo', safeRedirect)
  return { url: `/${slug}/sign-in?${params.toString()}` }
}
